import { Link, Navigate } from 'react-router-dom';
import type { ReactNode } from 'react';
import {
  ACCOUNT_TYPE_LABEL,
  ACCOUNT_TYPE_ORDER,
  CHART_OF_ACCOUNTS,
  HELP_ARTICLES,
} from '../content/site';
import { POS_URL, SIGNUP_URL, WEB_URL } from '../config';

function prose(text: string) {
  return text.split('\n\n').map((block, i) => {
    if (block.startsWith('**') && block.includes('**')) {
      const parts = block.split(/\*\*(.*?)\*\*/g);
      return (
        <p key={i}>
          {parts.map((p, j) => (j % 2 === 1 ? <strong key={j}>{p}</strong> : p))}
        </p>
      );
    }
    if (block.startsWith('• ')) {
      const items = block.split('\n').filter(Boolean);
      return (
        <ul key={i}>
          {items.map((li) => (
            <li key={li}>{li.replace(/^•\s*/, '')}</li>
          ))}
        </ul>
      );
    }
    return <p key={i}>{block}</p>;
  });
}

function ChartTable() {
  return (
    <>
      {ACCOUNT_TYPE_ORDER.map((type) => {
        const rows = CHART_OF_ACCOUNTS.filter((a) => a.type === type);
        if (!rows.length) return null;
        return (
          <div key={type} className="mb-6">
            <h3 className="mb-2 text-[0.75rem] font-bold tracking-widest text-ink-3 uppercase">
              {ACCOUNT_TYPE_LABEL[type]}
            </h3>
            <table className="account-table">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Account</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((a) => (
                  <tr key={a.code}>
                    <td className="code">{a.code}</td>
                    <td>{a.parentCode ? `↳ ${a.name}` : a.name}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })}
    </>
  );
}

const ARTICLE_BODY: Record<string, { title: string; lead: string; content: ReactNode }> = {
  'getting-started': {
    title: 'Getting started',
    lead: 'From signup to first sale — quick setup, then add products when you are ready.',
    content: (
      <div className="prose">
        {prose(`**1. Create your business**
Sign up in the Business app with your name, business name, and email. Verify your inbox, then complete onboarding — business type, country, and currency.

**2. Add products**
Add what you sell with a name and price. They appear on POS. Track stock quantities when your business needs them — stock counts are optional.

**3. Ring your first sale**
Open POS, sign in, open a session if prompted, tap products to cart, choose payment method, and charge. Stock and money update from that sale when those areas are in use.

**4. Optional next steps**
Invite staff in Settings, configure tax when you need it, or create a purchase order in Buying when you restock.

Need tax? Enable VAT in Settings before heavy trading so cart and ledger stay aligned.`)}
        <p>
          <a href={SIGNUP_URL}>Create account</a> · <a href={POS_URL}>Open POS</a> · <a href={WEB_URL}>Business app</a>
        </p>
      </div>
    ),
  },
  accounting: {
    title: 'How cevop books your money',
    lead: 'Every organisation gets the same default chart of accounts. Balances live in Reports — this page explains the structure and common postings.',
    content: (
      <div className="prose">
        <p>
          cevop uses double-entry bookkeeping. When you sell, spend, or close a POS session with a difference, the system writes balanced journal entries to these accounts. You do not pick accounts on POS — posting rules map actions to the chart automatically.
        </p>
        <h2>Default chart of accounts</h2>
        <ChartTable />
        <h2>Counter sale (cash, VAT exclusive)</h2>
        <p>Example: ₦11,000 net + ₦825 VAT = ₦11,825 total paid in cash.</p>
        <ul>
          <li>Debit 1000 Cash on hand — ₦11,825</li>
          <li>Credit 4000 Sales revenue — ₦11,000</li>
          <li>Credit 2100 VAT payable — ₦825</li>
        </ul>
        <h2>Inventory sale</h2>
        <p>When stock is tracked, cevop also posts cost of goods sold against inventory at your recorded cost.</p>
        <h2>Expense</h2>
        <p>Recording an expense debits the category account (6100–6900) and credits cash or bank depending on payment method.</p>
        <h2>POS close short/over</h2>
        <p>If counted cash differs from expected, the gap posts to 3200 Cash short/over so your cash account matches what you counted.</p>
        <p>Export the ledger and financial pack from Reports in the Business app. Custom accounts are not editable in-product yet — contact sales if you need Enterprise.</p>
      </div>
    ),
  },
  tax: {
    title: 'Tax at the register',
    lead: 'How organisation tax settings flow to POS and the ledger.',
    content: (
      <div className="prose">
        {prose(`Enable tax under Organisation → Tax settings in the Business app. Choose **exclusive** (tax added at checkout) or **inclusive** (prices already include tax).

**Exclusive mode** — Product prices are net. The POS cart shows subtotal, VAT line, and total. Charge amount matches what posts to sales and VAT payable.

**Inclusive mode** — Shelf prices include tax. The cart shows one total to the cashier; the server still splits net and VAT for the ledger.

**Off** — No tax lines on cart or journal (unless you later enable tax).

Cashiers see totals computed with the same rules as the server before they charge — if they ever mismatch, POS blocks the sale and asks them to contact support.

Tax ID and rate are organisation-wide; branch overrides follow the same effective-tax rules as counter sales.`)}
      </div>
    ),
  },
  pos: {
    title: 'POS',
    lead: 'Why you open and close the drawer every shift.',
    content: (
      <div className="prose">
        {prose(`Each cashier can hold **one open POS session per branch**. The register is blocked until they declare opening cash.

During the shift, **cash** sales increase expected drawer cash. Card and transfer sales do not — they still post to revenue but not to the cash-on-hand expectation.

To close: count physical cash, enter the total, and confirm. cevop shows expected vs counted and the short or over amount. Optional notes explain personal change given or drops.

Branch managers review all sessions in the Business app — filter by gap, export CSV/XLSX/PDF for reconciliation.

If a cashier forgets to close, they cannot open a second session — sign back in and close the open shift first.`)}
      </div>
    ),
  },
  security: {
    title: 'Security & data',
    lead: 'How cevop protects accounts, tenants, and sensitive actions.',
    content: (
      <div className="prose">
        {prose(`**Tenant isolation** — Every query is scoped to your organisation. One shop never sees another's data.

**Roles & permissions** — Cashiers, branch managers, managers, accountants, and owners have different capabilities. Void and refund require branch-manager (or higher) permissions on POS.

**Authentication** — Passwords are hashed. Sessions use short-lived access tokens and rotating refresh tokens. Sign out invalidates the session.

**Step-up auth** — Sensitive changes in the Business app (where enabled) can require re-entering your password.

**Audit** — Voids, refunds, plan changes, and admin actions leave audit trails.

**Transport** — All traffic is HTTPS. We do not store card numbers — POS records payment method labels only.

**Backups & availability** — We run on managed infrastructure with backups and monitoring. No system is perfect; export reports regularly for your records.

Report a concern: security@cevop.com`)}
        <p><Link to="/privacy">Privacy Policy</Link></p>
      </div>
    ),
  },
};

export function HelpArticlePage({ slug }: { slug: string }) {
  const meta = HELP_ARTICLES.find((a) => a.slug === slug);
  const body = ARTICLE_BODY[slug];
  if (!meta || !body) return <Navigate to="/help" replace />;

  return (
    <>
      <section className="border-b border-line px-6 pt-28 pb-10 lg:px-10 lg:pt-36">
        <div className="mx-auto max-w-[1280px]">
          <p className="mb-3 text-[0.72rem] font-bold tracking-widest text-brand uppercase">Help</p>
          <h1 className="font-display mb-3 text-[2.25rem] font-extrabold text-ink">{body.title}</h1>
          <p className="max-w-[58ch] text-[1.05rem] text-ink-3">{body.lead}</p>
        </div>
      </section>
      <section className="px-6 py-12 lg:px-10">
        <div className="mx-auto grid max-w-[1280px] gap-10 lg:grid-cols-[220px_1fr]">
          <aside className="text-sm lg:sticky lg:top-24 lg:self-start">
            <Link to="/help" className="mb-3 block text-ink-3 hover:text-brand">
              ← All articles
            </Link>
            {HELP_ARTICLES.map((a) => (
              <Link
                key={a.slug}
                to={`/help/${a.slug}`}
                className={`block py-1 ${a.slug === slug ? 'font-semibold text-brand' : 'text-ink-3'}`}
              >
                {a.title}
              </Link>
            ))}
          </aside>
          <div>{body.content}</div>
        </div>
      </section>
    </>
  );
}
